import { ChevronDown, RotateCcw, SlidersHorizontal } from 'lucide-react'
import { createPortal } from 'react-dom'
import type { ComponentProps } from 'react'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'
import { BOX_TRUCK_MAX_POSTED_AGE_OPTIONS_HOURS, sanitizeBoxTruckMaxPostedAgeHours } from '@numeo/types-core'
import { BEON_SOURCE, loadTypeOrBoth } from '../hooks/requestMapping'
import { sourceGroupLabel } from '../utils/loadFields'
import { AdvancedSearchValues, countActiveAdvanced } from '../utils/boxTruckAdvancedFilterState'
import { Switch } from '@/components/ds2'
import { boxTruckDatSelection } from '@numeo/types-core'
import { BoxTruckFilterSelect } from './BoxTruckFilterSelect'
import { BoxTruckDatEquipmentSection } from './BoxTruckDatEquipmentSection'
import type { BoxTruckRefinements } from './BoxTruckRefinementBar'

/** 'Both' first: it is the default and means "don't filter", so the reset is the top option — same order
 *  and wording as Load Hub's picker. */
/** No "All": the request's bound is what keeps the search off a full-table scan — see the options constant. */
const AGE_OPTIONS = BOX_TRUCK_MAX_POSTED_AGE_OPTIONS_HOURS.map((hours) => ({ label: `${hours}h`, value: String(hours) }))

const LOAD_TYPE_OPTIONS = [
    { label: 'Both', value: 'BOTH' },
    { label: 'Full', value: 'FULL' },
    { label: 'Partial', value: 'PARTIAL' },
]

const CONTACT_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'Has email', value: 'email' },
    { label: 'Has phone', value: 'phone' },
]

/** A labelled row inside the panel. */
const Row = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
    <div className="flex flex-col gap-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">
            {label}
            {hint && <span className="ml-1 font-medium normal-case tracking-normal opacity-70">{hint}</span>}
        </span>
        {children}
    </div>
)

/** One compact numeric input — digits and a single decimal point, same keystroke rule as the visible row. */
const NumInput = ({ label, title, value, onChange }: { label: string; title: string; value: string; onChange: (v: string) => void }) => (
    <label className="flex h-9 cursor-text items-center rounded-lg bg-background px-2.5 shadow-field focus-within:ring-2 focus-within:ring-accent">
        <span className="pr-2 text-[10px] font-medium text-foreground-muted">{label}</span>
        <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => {
                const v = e.target.value
                if (v === '' || /^\d*\.?\d*$/.test(v)) onChange(v)
            }}
            aria-label={title}
            title={title}
            className="h-full w-full min-w-0 bg-transparent text-xs font-medium tabular-nums text-foreground outline-none"
        />
    </label>
)

/**
 * Advanced Filters — the narrow, rarely-touched controls, behind one disclosure so the default board reads
 * as origin / destination / equipment / pickup and nothing else.
 *
 * The panel deliberately keeps two GROUPS apart rather than presenting six equivalent boxes. Load Type is
 * part of the SEARCH and only takes effect when the operator presses Search. The numeric refinements apply
 * to the rows already on the board, live
 * as they are typed. Mixing them into one flat list would make half the panel look broken — you type a
 * value and nothing happens until you remember to search.
 */
export const BoxTruckAdvancedFilters = ({
    search,
    onSearchChange,
    refinements,
    refinementDefaults,
    onRefinementsChange,
    onReset,
    datEnabled,
    onDatEnabledChange,
    showDatSource,
    showMaxAge,
    equipmentLabels,
    datEquipmentTypes,
    onDatEquipmentTypesChange,
    beonEnabled,
    onBeonEnabledChange,
    beonConnected,
    onBeonConnect,
}: {
    search: AdvancedSearchValues
    onSearchChange: (patch: Partial<AdvancedSearchValues>) => void
    refinements: BoxTruckRefinements
    /** The board's starting values, so the badge never counts a default as an operator's filter. */
    refinementDefaults: BoxTruckRefinements
    onRefinementsChange: (patch: Partial<BoxTruckRefinements>) => void
    /** Clears ONLY what this panel owns — the visible row's own Reset is unaffected. Deliberately does NOT
     *  touch the DAT switch: Clear is for filters that NARROW the board, and silently cutting a feed the
     *  operator turned on is not a narrowing. */
    onReset: () => void
    /** Whether the board asks for the live DAT supplement at all. Off by default; see useBoxTruckDatEnabled. */
    datEnabled: boolean
    onDatEnabledChange: (enabled: boolean) => void
    showDatSource: boolean
    /** Driver Bids hides the Search action, so a control that only takes effect on Search has nothing to act
     *  on there — and its Clear leaves search values alone, which would strand a badge nothing could clear. */
    showMaxAge: boolean
    equipmentLabels: string[]
    datEquipmentTypes: string[]
    onDatEquipmentTypesChange: ComponentProps<typeof BoxTruckDatEquipmentSection>['onSelectedCodesChange']
    /** Whether the board asks for the operator's own BEON feed. Off by default; see useBoxTruckBeonEnabled. */
    beonEnabled: boolean
    onBeonEnabledChange: (enabled: boolean) => void
    /** Whether this project HAS a BeOnCarrier connection — `null` while the status read is still in flight.
     *  With no connection there is nothing to switch on, so the row offers Connect instead of a switch that
     *  would silently return nothing. */
    beonConnected: boolean | null
    onBeonConnect: () => void
}) => {
    const { open, anchorRect, containerRef, triggerRef, panelRef, close, togglePanel, panelPositionStyle } = useAnchoredPopover({
        repositionOnResize: false,
        panelWidth: 340,
    })
    const activeCount =
        countActiveAdvanced(search, refinements, refinementDefaults, { countMaxAge: showMaxAge }) +
        (showDatSource && datEnabled && boxTruckDatSelection(equipmentLabels, datEquipmentTypes).narrowed ? 1 : 0)
    // Connect replaces the switch only once we KNOW there is no connection. While the status read is still in
    // flight an operator who already had the feed on keeps their switch — otherwise a connection that lapsed
    // would leave them looking at a source error with no control on screen to turn it off.
    const showBeonSwitch = beonConnected === true || (beonConnected === null && beonEnabled)
    const panelTop = typeof panelPositionStyle.top === 'number' ? panelPositionStyle.top : 8
    const panelMaxHeight = Math.max(240, window.innerHeight - panelTop - 8)

    return (
        <div ref={containerRef} className="inline-flex">
            <button
                ref={triggerRef}
                type="button"
                onClick={togglePanel}
                aria-expanded={open}
                title="Load type, size caps, rate and weight refinements"
                className={`flex h-8 items-center gap-1.5 rounded-3xl px-3 text-xs font-medium shadow-field transition-colors ${
                    activeCount > 0 ? 'bg-accent-soft text-accent' : 'bg-surface text-foreground-muted hover:text-foreground'
                }`}
            >
                <SlidersHorizontal size={12} /> Advanced Filters
                {/* Never let a hidden filter be a silent one. */}
                {activeCount > 0 && (
                    <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-background">
                        {activeCount}
                    </span>
                )}
                <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={panelRef}
                        style={{ ...panelPositionStyle, maxHeight: panelMaxHeight }}
                        className="overflow-y-auto rounded-2xl bg-surface p-3 shadow-overlay"
                    >
                        <div className="mb-2.5 flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-foreground">Advanced Filters</span>
                            {activeCount > 0 && (
                                <button
                                    type="button"
                                    onClick={onReset}
                                    className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-foreground-muted transition-colors hover:text-foreground"
                                >
                                    <RotateCcw size={11} /> Clear {activeCount}
                                </button>
                            )}
                        </div>

                        <div className="flex flex-col gap-2.5">
                            {/* The DAT switch itself is not counted in `activeCount`: it adds a feed instead of
                                narrowing the board. Exact subtype choices are counted, and the selector explains
                                when DAT is waiting for the operator to choose at least one type. */}
                            {showDatSource && (
                                <Row label="Sources" hint="· applied on Search">
                                    <div className="flex items-center justify-between gap-3 rounded-lg bg-background px-2.5 py-2 shadow-field">
                                        <span className="flex min-w-0 flex-col">
                                            <span className="text-xs font-medium text-foreground">DAT loads</span>
                                            <span className="text-[10px] leading-tight text-foreground-subtle">Needs an origin or destination</span>
                                        </span>
                                        <Switch size="sm" checked={datEnabled} onChange={onDatEnabledChange} ariaLabel="DAT loads" />
                                    </div>
                                    {datEnabled && (
                                        <BoxTruckDatEquipmentSection
                                            equipmentLabels={equipmentLabels}
                                            selectedCodes={datEquipmentTypes}
                                            onSelectedCodesChange={onDatEquipmentTypesChange}
                                        />
                                    )}
                                    {/* BEON sits beside DAT because it is the same KIND of control — one more lane-only
                                        feed unioned into the board — but it is the operator's OWN account, so until that
                                        connection exists the row offers Connect rather than a switch that would turn on
                                        a feed with nothing behind it. No equipment sub-picker: the BEON leg carries the
                                        board's top-level equipment and LIS maps it. */}
                                    <div className="flex items-center justify-between gap-3 rounded-lg bg-background px-2.5 py-2 shadow-field">
                                        <span className="flex min-w-0 flex-col">
                                            {/* The board's OWN name for this source, so the switch and the Sources-bar
                                                chip that counts its rows cannot end up calling it two things. */}
                                            <span className="text-xs font-medium text-foreground">{sourceGroupLabel(BEON_SOURCE)} loads</span>
                                            <span className="text-[10px] leading-tight text-foreground-subtle">
                                                {showBeonSwitch ? 'Needs an origin or destination' : 'Connect your BeOnCarrier account'}
                                            </span>
                                        </span>
                                        {showBeonSwitch ? (
                                            <Switch
                                                size="sm"
                                                checked={beonEnabled}
                                                onChange={onBeonEnabledChange}
                                                ariaLabel={`${sourceGroupLabel(BEON_SOURCE)} loads`}
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    // Close this panel FIRST, or it paints over the connect
                                                    // modal the operator just asked to see. Not a race: this
                                                    // popover sits at POPOVER_Z_INDEX (10000), deliberately
                                                    // above the ds2 overlay layer, while AihubAuthModal is a
                                                    // Joy `Dialog` at Joy's own modal z-index (~1300). The
                                                    // panel wins every time, so it has to be gone.
                                                    close()
                                                    onBeonConnect()
                                                }}
                                                className="shrink-0 rounded-3xl bg-accent px-2.5 py-1 text-[11px] font-semibold text-background transition-opacity hover:opacity-90"
                                            >
                                                Connect
                                            </button>
                                        )}
                                    </div>
                                </Row>
                            )}
                            <Row label="Load Type" hint="· applied on Search">
                                <BoxTruckFilterSelect
                                    value={search.fullPartial}
                                    onChange={(v) => onSearchChange({ fullPartial: loadTypeOrBoth(v) })}
                                    options={LOAD_TYPE_OPTIONS}
                                    className="w-full"
                                />
                            </Row>
                            {/* On Search, not instantly: this is the REQUEST's own maxPostedAgeHours, so it changes which
                                loads the server returns rather than hiding rows already on the board. Applying it
                                instantly would be the old cap again — a client filter over a page the server picked on
                                a different clock.

                                Which is why Driver Bids does not get it: that view has no Search action to apply it
                                with, and its Clear deliberately leaves search values alone, so the control would badge
                                a filter the board neither applies nor can undo. */}
                            {showMaxAge && (
                                <Row label="Max Age" hint="· applied on Search">
                                    <BoxTruckFilterSelect
                                        value={String(search.maxPostedAgeHours)}
                                        onChange={(v) => onSearchChange({ maxPostedAgeHours: sanitizeBoxTruckMaxPostedAgeHours(v) })}
                                        options={AGE_OPTIONS}
                                        className="w-full"
                                    />
                                </Row>
                            )}
                            <div className="my-0.5 border-t border-border" />

                            <Row label="Rate" hint="· applied instantly">
                                <div className="grid grid-cols-2 gap-2">
                                    <NumInput
                                        label="Min $"
                                        title="Minimum rate (USD)"
                                        value={refinements.minR}
                                        onChange={(v) => onRefinementsChange({ minR: v })}
                                    />
                                    <NumInput
                                        label="$/mi"
                                        title="Minimum rate per mile"
                                        value={refinements.minT}
                                        onChange={(v) => onRefinementsChange({ minT: v })}
                                    />
                                </div>
                            </Row>
                            <Row label="Weight" hint="· applied instantly">
                                <div className="grid grid-cols-2 gap-2">
                                    <NumInput
                                        label="Min lbs"
                                        title="Minimum weight (lbs)"
                                        value={refinements.minWeight}
                                        onChange={(v) => onRefinementsChange({ minWeight: v })}
                                    />
                                    <NumInput
                                        label="Max lbs"
                                        title="Maximum weight (lbs)"
                                        value={refinements.maxWeight}
                                        onChange={(v) => onRefinementsChange({ maxWeight: v })}
                                    />
                                </div>
                            </Row>
                            <Row label="Contact" hint="· only loads that have it">
                                <BoxTruckFilterSelect
                                    value={refinements.contact}
                                    onChange={(value) => onRefinementsChange({ contact: value })}
                                    options={CONTACT_OPTIONS}
                                    className="w-full"
                                />
                            </Row>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    )
}
